/**
 * @file index.tsx
 * @author liuchen(loocliu@tencent.com)
 */

import TencentCloudChat from '@tencentcloud/chat';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _, { uniqBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DeleteIcon } from 'tdesign-icons-react';
import { DialogPlugin, Loading, MessagePlugin } from 'tdesign-react';
import TIMUploadPlugin from 'tim-upload-plugin';

import Back from '@/components/back';
import Messages from '@/components/messages';
import { RichMediaShowType } from '@/const/richMedia';
import { RichMediaValue } from '@/interface/media';
import { fetchUserSig } from '@/services/auth';
import { chatPrivate, deleteBotMemory } from '@/services/bot';
import { useAppDispatch, useTypedSelector } from '@/store';
import { ContentType, ConversationType, Direction, EventType, IEvent, IIMMessage, IOriIMMessage, SenderType } from '@/store/modules/__endpoints__/imMessage';
import { setDisabled } from '@/store/modules/bot';
import { setChat } from '@/store/modules/chat';
import { setHexToRgba } from '@/utils/color';
import { getImSDKAppId, isLocal, isWoa } from '@/utils/getApiDomain';
import { transformObjectDeep } from '@/utils/tans/object';

import Footer from './footer';
import styles from './style/index.module.less';

interface IPrivateChatProps {
  id?: string;
  chatBackground?: RichMediaValue;
  name?: string;
  avatar?: RichMediaValue;
  talking?: string;
  style?: React.CSSProperties
}

const PrivateChat = (props: IPrivateChatProps) => {
  const { id, chatBackground, style, name, talking, avatar } = props;
  const dispatch = useAppDispatch();

  const { chat } = useTypedSelector(store => store.chat);
  const { vuid } = useTypedSelector(store => store.user);
  const { woaUser } = useTypedSelector(store => store.auth);
  const { disabled } = useTypedSelector(store => store.bot);

  const [messages, setMessages] = useState<(IIMMessage & { isUp?: boolean })[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [next, setNext] = useState<{ nextTime?: number; isCompleted?: boolean }>({});

  const ref = useRef<any>();

  const getMessages = useCallback(
    (direction: Direction, next?: { nextTime?: number; isCompleted?: boolean }, callback?: Function) => {
      if (chat === undefined || !id || next?.isCompleted === true) {
        return;
      }
      chat
        .getMessageListHopping({
          conversationID: `C2C@RBT#${id}`,
          direction,
          time: next?.nextTime,
        })
        .then((imResponse: {
          data: {
            messageList: IOriIMMessage[];
            isCompleted: boolean; nextMessageTime: number
          }
        }) => {
          const { messageList, isCompleted, nextMessageTime } = imResponse.data;
          const newMsgs: IIMMessage[] = messageList.map((item) => {
            let parsedData = {};
            try {
              parsedData = transformObjectDeep(JSON.parse(item.payload.data), 'camel');
            } catch { }
            return {
              ...item,
              payload: { data: parsedData as IEvent },
            };
          });
          setDelLoading(false);
          dispatch(setDisabled(false));
          if (direction === Direction.up) {
            setNext({ nextTime: nextMessageTime, isCompleted });
            setMessages(messages => uniqBy([...newMsgs, ...messages], 'ID'));
          } else {
            setMessages(messages => uniqBy([...messages, ...newMsgs], 'ID'));
          }
          if (callback) {
            callback();
          }
        });
    },
    [chat, id],
  );

  const handleSubmit = useCallback(
    (value: string) => {
      if (chat === undefined || !id || (!vuid && !isWoa) || (isWoa && !woaUser)) {
        if (!id) {
          MessagePlugin.info('请点击保存创建角色后开启聊天');
        }
        return;
      }
      const msgContent: IEvent = {
        type: EventType.EVENT_TYPE_MESSAGE,
        message: {
          type: ContentType.CONTENT_TYPE_TEXT,
          sender: {
            senderId: isWoa ? woaUser || '' : `cms_${vuid}`,
            type: SenderType.SENDER_TYPE_USER,
          },
          text: { text: value, showType: RichMediaShowType.SHOW_TYPE_INLINE },
        },
        isFinish: true,
      };
      dispatch(setDisabled(true));
      const msg = chat.createCustomMessage({
        to: `@RBT#${id}`,
        conversationType: TencentCloudChat.TYPES.CONV_C2C,
        payload: {
          data: JSON.stringify(transformObjectDeep(msgContent, 'snake')),
        },
      });
      chat
        .sendMessage(msg)
        .then(() => {
          setMessages(messages => [
            ...messages,
            {
              ID: dayjs().valueOf()
                .toString(),
              conversationID: `C2C@RBT#${id}`,
              conversationType: ConversationType.C2C,
              avatar: '',
              nick: '',
              to: id || '',
              from: isWoa ? woaUser || '' : `cms_${vuid}`,
              flow: 'out',
              payload: { data: msgContent },
              isUp: false,
            },
          ]);
          setIsLoading(true);
          chatPrivate({
            userId: (isWoa ? woaUser : `cms_${vuid}`) || '',
            chatMessage: value,
            botsAt: [`@RBT#${id}`],
          });
        })
        .catch((imError: any) => {
          MessagePlugin.error(imError);
          dispatch(setDisabled(false));
        });
    },
    [chat, id, getMessages, vuid, isWoa],
  );

  const handleLoadBefore = useCallback(() => {
    getMessages(Direction.up, next);
  }, [getMessages, next]);

  const restartChat = useCallback(async () => {
    if (delLoading || disabled) {
      return;
    }
    if (chat && id) {
      try {
        setDelLoading(true);
        dispatch(setDisabled(true));
        const { code, message, msg } = await deleteBotMemory({
          fromUserId: isWoa ? woaUser || '' : `cms_${vuid}`,
          toUserId: id,
        });
        if (code !== 0) {
          MessagePlugin.error(message || msg || '后台服务错误');
          setDelLoading(false);
          dispatch(setDisabled(false));
          return;
        }
        const { data } = await chat.getConversationProfile(`C2C@RBT#${id}`);
        if (data.conversation) {
          await chat.clearHistoryMessage(`C2C@RBT#${id}`);
        }
        await chat.deleteFriend({
          userIDList: [`@RBT#${id}`],
          type: TencentCloudChat.TYPES.SNS_DELETE_TYPE_BOTH,
        });
      } catch (error) {
        console.log(JSON.stringify(error));
      } finally {
      }
    }
  }, [chat, id, vuid, woaUser, isWoa, delLoading, disabled]);

  useEffect(() => {
    if (!id) {
      setMessages((talking || '').split('\n').map((item, idx) => ({
        ID: `${dayjs().unix()}/${idx}`,
        flow: 'in',
        nick: name || '',
        payload: {
          data: {
            type: EventType.EVENT_TYPE_MESSAGE,
            message: { text: { text: item } },
          },
        },
      } as any)));
      return () => {
        setMessages([]);
      };
    }
  }, [id, name, talking]);

  const addFriend = useCallback(async () => {
    if (chat && id) {
      try {
        const { data } = await chat.getFriendList();
        const friendIds = (data || []).map((item: { userID: string }) => item.userID);
        if (!friendIds.includes(`@RBT#${id}`)) {
          await chat.addFriend({
            to: `@RBT#${id}`,
            source: 'AddSource_Type_Web',
            type: TencentCloudChat.TYPES.SNS_ADD_TYPE_BOTH,
          });
        } else {
          getMessages(Direction.up);
        }
      } catch (error) {
      }
    }
  }, [chat, id]);

  useEffect(() => {
    addFriend();
  }, [addFriend]);

  useEffect(() => {
    if (chat !== undefined || (!vuid && !isWoa) || (isWoa && !woaUser)) {
      return;
    }
    const newChat = TencentCloudChat.create({
      SDKAppID: getImSDKAppId(),
    });
    newChat.setLogLevel(isLocal ? 0 : 3);
    newChat.registerPlugin({ 'tim-upload-plugin': TIMUploadPlugin });
    const saveChat = () => {
      dispatch(setChat({ chat: newChat }));
    };
    const kickedOutMsg = () => {
      const dialog = DialogPlugin.alert({
        header: <div>掉线了！</div>,
        children: '多端登录，已被踢出，请刷新页面重置聊天登录状态',
        onClose: () => dialog.hide(),
        onConfirm: () => dialog.hide(),
      });
    };
    newChat.on(TencentCloudChat.EVENT.SDK_READY, saveChat);
    newChat.on(TencentCloudChat.EVENT.KICKED_OUT, kickedOutMsg);
    fetchUserSig({ expiration: 86400 }).then((res) => {
      if (!_.isEmpty(res)) {
        newChat.login({ userID: isWoa ? woaUser || '' : `cms_${vuid}`, userSig: res });
      }
    });
  }, [chat, vuid, isWoa, woaUser]);

  useEffect(() => {
    if (chat && id) {
      const handleMsgReceived1 = (e: { name: string; data: IOriIMMessage[] }) => {
        const { data } = e;
        if (data.length === 0) {
          return;
        }
        let parsedData = {};
        try {
          parsedData = JSON.parse(data[0].payload.data);
        } catch { }
        setIsLoading(false);
        setDelLoading(false);
        const newMsg: IIMMessage = transformObjectDeep(
          {
            ...data[0],
            payload: { data: parsedData },
          },
          'camel',
        );
        if (newMsg.payload.data.isFinish === true) {
          dispatch(setDisabled(false));
        }
        if (newMsg.conversationID === `C2C@RBT#${id}`) {
          setMessages(messages => [...messages, newMsg]);
        }
      };
      const handleMsgReceived2 = (e: { name: string; data: IOriIMMessage[] }) => {
        const { data } = e;
        if (data.length === 0) {
          return;
        }
        let parsedData = {};
        try {
          parsedData = JSON.parse(data[0].payload.data);
        } catch { }
        setIsLoading(false);
        const newMsg: IIMMessage = transformObjectDeep(
          {
            ...data[0],
            payload: { data: parsedData },
          },
          'camel',
        );
        if (newMsg.conversationID === `C2C@RBT#${id}`) {
          if (newMsg.payload.data.isFinish === true) {
            dispatch(setDisabled(false));
          }
          setMessages((messages) => {
            const lastMsgIdx = messages.findIndex(({ ID }) => ID === newMsg.ID);
            return [...messages.slice(0, lastMsgIdx), newMsg, ...messages.slice(lastMsgIdx + 1)];
          });
          setAutoPlay(true);
        }
      };
      const handleFriendListUpdated = (e: { data: { userID: string }[] }) => {
        const friendIds = e.data.map(({ userID }) => userID);
        if (!friendIds.includes(`@RBT#${id}`)) {
          setMessages([]);
          addFriend();
        } else {
          getMessages(Direction.up);
        }
      };
      chat.on(TencentCloudChat.EVENT.MESSAGE_RECEIVED, handleMsgReceived1);
      chat.on(TencentCloudChat.EVENT.MESSAGE_MODIFIED, handleMsgReceived2);
      chat.on(TencentCloudChat.EVENT.FRIEND_LIST_UPDATED, handleFriendListUpdated);
      return () => {
        chat.off(TencentCloudChat.EVENT.MESSAGE_RECEIVED, handleMsgReceived1);
        chat.off(TencentCloudChat.EVENT.MESSAGE_MODIFIED, handleMsgReceived2);
        chat.off(TencentCloudChat.EVENT.FRIEND_LIST_UPDATED, handleFriendListUpdated);
      };
    }
  }, [chat, id]);

  useEffect(() => {
    if (autoPlay && messages[messages.length - 1]?.payload.data.isFinish) {
      ref.current?.play(messages[messages.length - 1].ID);
      setAutoPlay(false);
    }
  }, [autoPlay, messages]);

  return <div className={styles.root} style={{
    ...style, background: `url("${chatBackground
      ?.url?.url || ''}") lightgray 50% / cover no-repeat`,
  }}>
    {!_.isEmpty(chatBackground) && <div className={styles.mask} style={{
      background: chatBackground ? `linear-gradient(180deg, ${setHexToRgba(chatBackground?.sample?.primary || '', 0.0)} 17.82%, ${setHexToRgba(chatBackground?.sample?.primary || '', 0.01)} 39.58%, ${chatBackground?.sample?.primary || ''} 68.68%,  ${chatBackground?.sample?.primary || ''} 99.92%)` : 'transparent',
    }} />}
    <Back />
    <div className={classNames({
      [styles.clear]: 1,
      [styles['clear-disabled']]: disabled || delLoading || id == null,
    })}
      onClick={restartChat}>
      {delLoading ? <Loading size='10px' /> : <DeleteIcon />}
    </div>
    <Messages
      ref={ref}
      data={messages}
      isLoading={isLoading}
      visible={true}
      avatar={avatar}
      primary={chatBackground?.sample?.primary || ''}
      style={{
        position: 'absolute',
        bottom: '85px',
        maxHeight: 'calc(100% - 85px - 110px)',
      }}
      onLoadBefore={handleLoadBefore}
    />
    <Footer
      disabled={disabled}
      onSubmit={handleSubmit}
    />
  </div>;
};

export default PrivateChat;
